<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { ref, watch } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';
import type {
  IBeforeLeavingEvents,
  IBeforeLeavingProps
} from '@/types/components/general/BeforeLeaving';
import { useBeforeLeave } from '@/composables/useBeforeLeave';

const { t } = useI18n();

const props = withDefaults(defineProps<IBeforeLeavingProps>(), {
  dialogPopup: false,
  leaveSubmit: false,
  form: 'dialog'
});

const emit = defineEmits<IBeforeLeavingEvents>();

const cancelLeave = ref(false);
const dontSave = ref(false);
const displayDialog = ref(false);

const { dialogLeave } = useBeforeLeave();

const showUnsaveDialog = () => {
  cancelLeave.value = false;
  dontSave.value = false;
  displayDialog.value = true;
  return new Promise((resolve, reject) => {
    const checkUserInput = () => {
      if (cancelLeave.value) {
        displayDialog.value = false;
        reject();
      } else if (dontSave.value) {
        resolve(true);
      } else {
        setTimeout(checkUserInput, 250);
      }
    };
    checkUserInput();
  });
};

const afterLeave = () => {
  cancelLeave.value = true;
  dialogLeave(false);
};

onBeforeRouteLeave((to, from, next) => {
  if (props.form === 'view' && !props.leaveSubmit) {
    return showUnsaveDialog()
      .then(() => {
        next();
      })
      .catch(() => {
        next(false);
      });
  } else {
    next();
  }
});

watch(props, () => {
  if (props.dialogPopup) {
    return showUnsaveDialog()
      .then(() => {
        displayDialog.value = false;
        emit('leave', true);
      })
      .catch(() => {
        emit('leave', false);
      });
  }
});
</script>

<template>
  <div>
    <v-dialog v-model="displayDialog" color="primary" @after-leave="afterLeave">
      <template #default>
        <v-card sentry-unmask>
          <v-card-title>
            {{
              props.form === 'view'
                ? t('beforeLeave.saveBeforeLeaving')
                : t('beforeLeave.saveBeforeLeavingModal')
            }}
          </v-card-title>
          <v-card-actions>
            <OutlinedBtn data-test-id="before-leave-dont-save" @click="dontSave = !dontSave">
              {{ props.form === 'view' ? t('common.leave') : t('common.close') }}
            </OutlinedBtn>
            <FilledBtn data-test-id="before-leave-cancel" @click="cancelLeave = !cancelLeave">
              {{ t('common.cancel') }}
            </FilledBtn>
          </v-card-actions>
        </v-card>
      </template>
    </v-dialog>
  </div>
</template>
